import React from 'react';
import { LogOut, User, Menu, X, Moon, Sun, Settings, Home, MessageSquare, Calculator, ListChecks, Book, Newspaper, Users, UserPlus, Sparkles, CalendarHeart } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { useTheme } from '../lib/theme-context';
import { Menu as MenuComponent } from './ui/Menu';
import { FeedbackWidget } from './FeedbackWidget';
import { MobileNavBar } from './MobileNavBar';
import { MessageCenter } from './MessageCenter/MessageCenter';
import { NotificationBell } from './NotificationBell';
import { useUnreadMessages } from '../hooks/useUnreadMessages';
import { useNewItems } from '../hooks/useNewItems';
import * as amplitude from '@amplitude/analytics-browser';

interface NavbarProps {
  onLogin: () => void;
  isBudgetPage?: boolean;
  countryCode?: string;
}

export function Navbar({ onLogin, isBudgetPage, countryCode }: NavbarProps) {
  const { user, userInfo, isAdmin, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [showFeedback, setShowFeedback] = React.useState(false);
  const [showMessageCenter, setShowMessageCenter] = React.useState(false);
  const [notificationThread, setNotificationThread] = React.useState<{ code: string; type: 'country' | 'general' | 'bug' } | null>(null);
  const [showUserMenu, setShowUserMenu] = React.useState(false);
  const [hasSeenMenuHint, setHasSeenMenuHint] = React.useState(() => {
    return localStorage.getItem('hasSeenMenuHint') === 'true';
  });
  const [scrolled, setScrolled] = React.useState(false);
  const { hasUnread: hasUnreadMessages, refresh: refreshUnread } = useUnreadMessages();
  const { hasNewMatching, hasNewActivities } = useNewItems();

  React.useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  React.useEffect(() => {
    if (!hasSeenMenuHint) {
      const timer = setTimeout(() => {
        localStorage.setItem('hasSeenMenuHint', 'true');
        setHasSeenMenuHint(true);
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [hasSeenMenuHint]);

  const handleMenuToggle = () => {
    if (!isMenuOpen) {
      amplitude.track("Menu Clicked");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (!hasSeenMenuHint) {
        localStorage.setItem('hasSeenMenuHint', 'true');
        setHasSeenMenuHint(true);
      }
    }
    setIsMenuOpen(!isMenuOpen);
  };

  const handleSimulatorClick = () => {
    window.scrollTo(0, 0);
    navigate(isBudgetPage ? '/' : '/simulateur-budget', { replace: true });
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.user-menu') && !target.closest('.user-avatar')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayName = userInfo?.user?.username || user?.displayName || '';

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl shadow-lg shadow-gray-200/20 dark:shadow-black/20'
            : 'bg-white/60 dark:bg-gray-900/60 backdrop-blur-md'
        }`}
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between h-20 px-4 sm:px-6 lg:px-8">
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center group">
                <div className="relative">
                  <div className="absolute -inset-2 bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 rounded-2xl opacity-0 group-hover:opacity-20 blur-2xl transition-all duration-500"></div>
                  <img
                    src="https://pvtistes.net/wp-content/uploads/2025/01/rgb_pvtistes_logo_positive_fullcolor.png"
                    alt="Pvtistes"
                    className="h-11 w-auto dark:hidden transition-transform duration-300 group-hover:scale-105 relative z-10"
                  />
                  <img
                    src="https://pvtistes.net/wp-content/uploads/2025/01/rgb_pvtistes_logo_negative_white.png"
                    alt="Pvtistes"
                    className="h-11 w-auto hidden dark:block transition-transform duration-300 group-hover:scale-105 relative z-10"
                  />
                </div>
              </Link>
            </div>

            <div className="hidden lg:flex lg:items-center">
              {user ? (
                <div className="flex items-center gap-4">
                  <div className="flex items-center bg-gray-100/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-1.5 gap-1 shadow-sm">
                    <Link
                      to="/"
                      className="group relative flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 rounded-xl hover:bg-white dark:hover:bg-gray-700 hover:shadow-sm transition-all duration-200"
                    >
                      <ListChecks className="w-4 h-4 transition-transform group-hover:scale-110" />
                      <span>Checklist</span>
                    </Link>
                    <Link
                      to="/matching"
                      className="group relative flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 rounded-xl hover:bg-white dark:hover:bg-gray-700 hover:shadow-sm transition-all duration-200"
                    >
                      <UserPlus className="w-4 h-4 transition-transform group-hover:scale-110" />
                      <span>Compagnons</span>
                    </Link>
                    <Link
                      to="/activites"
                      className="group relative flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 rounded-xl hover:bg-white dark:hover:bg-gray-700 hover:shadow-sm transition-all duration-200"
                    >
                      <CalendarHeart className="w-4 h-4 transition-transform group-hover:scale-110" />
                      <span>Activités</span>
                    </Link>
                    <Link
                      to="/simulateur-budget"
                      className="group relative flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 rounded-xl hover:bg-white dark:hover:bg-gray-700 hover:shadow-sm transition-all duration-200"
                    >
                      <Calculator className="w-4 h-4 transition-transform group-hover:scale-110" />
                      <span>Budget</span>
                    </Link>
                    <button
                      onClick={() => setShowMessageCenter(true)}
                      className="group relative flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 rounded-xl hover:bg-white dark:hover:bg-gray-700 hover:shadow-sm transition-all duration-200"
                    >
                      <span className="relative">
                        <MessageSquare className="w-4 h-4 transition-transform group-hover:scale-110" />
                        {hasUnreadMessages && (
                          <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-gray-100 dark:ring-gray-800 animate-pulse" />
                        )}
                      </span>
                      <span>Messages</span>
                    </button>
                    <button
                      onClick={() => setIsMenuOpen(true)}
                      className="group relative flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 rounded-xl hover:bg-white dark:hover:bg-gray-700 hover:shadow-sm transition-all duration-200"
                    >
                      <Menu className="w-4 h-4 transition-transform group-hover:scale-110" />
                      <span>Plus</span>
                    </button>
                  </div>

                  <div className="flex items-center gap-2">
                    <NotificationBell onOpenMessageCenter={(threadCode, threadType) => {
                      setNotificationThread(threadCode ? { code: threadCode, type: threadType || 'general' } : null);
                      setShowMessageCenter(true);
                    }} />

                    <button
                      onClick={toggleTheme}
                      className="relative p-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100/80 dark:hover:bg-gray-800/80 rounded-xl transition-all duration-200 group backdrop-blur-sm"
                      title={theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'}
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-amber-400/20 to-orange-500/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                      {theme === 'dark' ? (
                        <Sun className="w-5 h-5 relative z-10 transition-transform group-hover:rotate-90 duration-300" />
                      ) : (
                        <Moon className="w-5 h-5 relative z-10 transition-transform group-hover:-rotate-12 duration-300" />
                      )}
                    </button>

                    <div className="relative">
                      <button
                        onClick={() => setShowUserMenu(!showUserMenu)}
                        className="user-avatar relative w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 flex items-center justify-center text-white text-sm font-bold hover:shadow-xl hover:shadow-emerald-500/30 hover:scale-105 transition-all duration-300 ring-2 ring-white/50 dark:ring-gray-800/50"
                      >
                        <span className="relative z-10">{displayName?.[0]?.toUpperCase()}</span>
                        <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-emerald-400 to-cyan-400 opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                      </button>

                      {showUserMenu && (
                        <div className="user-menu absolute right-0 mt-3 w-64 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden z-50 backdrop-blur-xl">
                          <div className="relative p-5 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 dark:from-emerald-900/20 dark:via-teal-900/20 dark:to-cyan-900/20 border-b border-gray-200/50 dark:border-gray-700/50">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-400/20 to-cyan-400/20 rounded-full blur-3xl"></div>
                            <div className="relative">
                              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 flex items-center justify-center text-white text-lg font-bold mb-3 shadow-lg">
                                {displayName?.[0]?.toUpperCase()}
                              </div>
                              <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
                                {displayName}
                              </p>
                              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 flex items-center gap-1">
                                <Sparkles className="w-3 h-3" />
                                Membre Pvtistes
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              logout();
                              setShowUserMenu(false);
                            }}
                            className="w-full flex items-center gap-3 px-5 py-4 text-sm font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          >
                            <LogOut className="w-4 h-4" />
                            <span>Déconnexion</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-4">
                  <div className="flex items-center bg-gray-100/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-1.5 gap-1 shadow-sm">
                    <Link
                      to="/"
                      className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 rounded-xl hover:bg-white dark:hover:bg-gray-700 hover:shadow-sm transition-all duration-200"
                    >
                      <ListChecks className="w-4 h-4" />
                      <span>Checklist</span>
                    </Link>
                    <Link
                      to="/simulateur-budget"
                      className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 rounded-xl hover:bg-white dark:hover:bg-gray-700 hover:shadow-sm transition-all duration-200"
                    >
                      <Calculator className="w-4 h-4" />
                      <span>Simulateur</span>
                    </Link>
                  </div>

                  <button
                    onClick={toggleTheme}
                    className="relative p-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100/80 dark:hover:bg-gray-800/80 rounded-xl transition-all duration-200 group backdrop-blur-sm"
                    title={theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-amber-400/20 to-orange-500/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    {theme === 'dark' ? (
                      <Sun className="w-5 h-5 relative z-10 transition-transform group-hover:rotate-90 duration-300" />
                    ) : (
                      <Moon className="w-5 h-5 relative z-10 transition-transform group-hover:-rotate-12 duration-300" />
                    )}
                  </button>

                  <button
                    onClick={onLogin}
                    className="relative group flex items-center gap-2.5 px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 hover:from-emerald-600 hover:via-teal-600 hover:to-cyan-600 text-white text-sm font-bold transition-all duration-300 transform hover:scale-105 hover:-translate-y-0.5 shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/50 overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                    <User className="w-4 h-4 relative z-10" />
                    <span className="relative z-10">Connexion</span>
                  </button>
                </div>
              )}
            </div>

            <div className="lg:hidden flex items-center gap-1">
              {user && (
                <>
                  <NotificationBell onOpenMessageCenter={(threadCode, threadType) => {
                    setNotificationThread(threadCode ? { code: threadCode, type: threadType || 'general' } : null);
                    setShowMessageCenter(true);
                  }} />
                  <button
                    onClick={() => setShowMessageCenter(true)}
                    className="relative p-2.5 text-gray-600 dark:text-gray-300 hover:bg-gray-100/80 dark:hover:bg-gray-800/80 rounded-xl transition-all duration-200"
                  >
                    <MessageSquare className="w-5 h-5" />
                    {hasUnreadMessages && (
                      <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-gray-900 animate-pulse" />
                    )}
                  </button>
                </>
              )}
              <button
                onClick={toggleTheme}
                className="p-2.5 text-gray-600 dark:text-gray-300 hover:bg-gray-100/80 dark:hover:bg-gray-800/80 rounded-xl transition-all duration-200"
                title={theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'}
              >
                {theme === 'dark' ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          <MenuComponent
            isOpen={isMenuOpen}
            onClose={() => setIsMenuOpen(false)}
          />
        </div>
      </nav>

      {user && (
        <MobileNavBar
          onOpenMenu={handleMenuToggle}
          isMenuOpen={isMenuOpen}
          hasNewMatching={hasNewMatching}
          hasNewActivities={hasNewActivities}
        />
      )}

      {showFeedback && <FeedbackWidget onClose={() => setShowFeedback(false)} />}

      {showMessageCenter && (
        <MessageCenter
          isOpen={true}
          onClose={() => {
            setShowMessageCenter(false);
            setNotificationThread(null);
            refreshUnread();
          }}
          countryCode={countryCode}
          initialThread={notificationThread || undefined}
        />
      )}

    </>
  );
}
